import { matricesApi } from '@/lib/api';
import { Alert } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';

export interface DerivedFromBannerProps {
  /** The matrix the draft was created from (`derivedFrom` search param). */
  sourceMatrixId?: string | undefined;
  /** The source's pivot was blank on a step and the draft fell back to Levier de valeur. */
  pivotReset?: boolean | undefined;
}

/**
 * The wizard's step 1 banners for a draft created with « Nouvelle matrice
 * depuis celle-ci » (issue #16).
 *
 * The source's sujet is read live rather than carried in the URL. A source
 * deleted since, or unreadable, simply shows no banner: it only informs, and
 * nothing in the draft depends on it. The pivot fallback message
 * (règle complémentaire 3) is about the draft itself, so it shows either way.
 */
export function DerivedFromBanner({ sourceMatrixId, pivotReset = false }: DerivedFromBannerProps) {
  const sourceQuery = useQuery({
    queryKey: ['matrix', sourceMatrixId],
    queryFn: () => matricesApi.get(sourceMatrixId ?? ''),
    enabled: sourceMatrixId !== undefined,
    retry: false,
  });
  const source = sourceQuery.data;
  const sourceSujet = source?.sujet?.trim();
  // A source still without a sujet (a draft) is named generically rather than
  // quoted as an empty « ».
  const sourceName = sourceSujet ? `« ${sourceSujet} »` : 'une autre matrice';

  if (!source && !pivotReset) return null;

  return (
    <div className="space-y-2">
      {source && (
        <Alert tone="info">
          {`Pré-rempli depuis ${sourceName} - le pivot, le destinataire et toutes les étapes restent modifiables.`}
        </Alert>
      )}
      {pivotReset && (
        <Alert tone="warning">
          Le pivot de la matrice source n'est plus renseigné pour toutes les étapes du setup : il a
          été remplacé par Levier de valeur.
        </Alert>
      )}
    </div>
  );
}
